import { useCallback, useState, useRef } from 'react';
import { notify } from '@/lib/notify';
import type { GifInfo, SendGifData } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function useGifs() {
  const [savedGifs, setSavedGifs] = useState<GifInfo[]>([]);
  const [searchResults, setSearchResults] = useState<GifInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [nextOffset, setNextOffset] = useState<string>('');
  const [hasMore, setHasMore] = useState(true);

  // Для отмены предыдущего поиска
  const searchAbortRef = useRef<AbortController | null>(null);

  /**
   * Загрузить сохранённые GIF
   */
  const fetchSavedGifs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/gifs`);

      const result: ApiResponse<GifInfo[]> = await response.json();
      if (result.success && result.data) {
        setSavedGifs(result.data);
      }
    } catch (_error) {
      notify.error('Ошибка загрузки сохранённых GIF');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Поиск GIF
   */
  const searchGifs = useCallback(async (query: string, loadMore = false) => {
    // Отменяем предыдущий поиск
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    searchAbortRef.current = new AbortController();

    if (!loadMore) {
      setSearchResults([]);
      setNextOffset('');
      setHasMore(true);
    }

    setIsSearching(true);
    try {
      const offset = loadMore ? nextOffset : '';
      const response = await fetch(
        `${API_URL}/api/telegram-client/gifs/search?q=${encodeURIComponent(query)}&offset=${encodeURIComponent(offset)}`,
        {
          signal: searchAbortRef.current.signal,
        }
      );

      const result: ApiResponse<{ results: GifInfo[]; nextOffset: string }> = await response.json();
      if (result.success && result.data) {
        if (loadMore) {
          setSearchResults(prev => [...prev, ...(result.data?.results ?? [])]);
        } else {
          setSearchResults(result.data?.results ?? []);
        }
        setNextOffset(result.data?.nextOffset ?? '');
        setHasMore(!!result.data?.nextOffset && (result.data?.results?.length ?? 0) > 0);
      }
    } catch (error) {
      // Игнорируем ошибки отмены
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      notify.error('Ошибка поиска GIF');
    } finally {
      setIsSearching(false);
    }
  }, [nextOffset]);

  /**
   * Отправить GIF в чат
   */
  const sendGif = useCallback(async (chatId: number, data: SendGifData): Promise<boolean> => {
    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/gifs/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: ApiResponse<unknown> = await response.json();
      if (!result.success) {
        notify.error(result.error || 'Ошибка отправки GIF');
        return false;
      }
      return true;
    } catch (_error) {
      notify.error('Ошибка сети');
      return false;
    } finally {
      setIsSending(false);
    }
  }, []);

  /**
   * Очистить результаты поиска
   */
  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setNextOffset('');
    setHasMore(true);
  }, []);

  return {
    savedGifs,
    searchResults,
    isLoading,
    isSearching,
    isSending,
    hasMore,
    fetchSavedGifs,
    searchGifs,
    sendGif,
    clearSearch,
  };
}
